import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Modal, Form, Spin, Select, InputNumber } from 'antd';
import { FaRegTrashAlt, FaEdit } from 'react-icons/fa';
import { PlusOutlined } from '@ant-design/icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Api from '../api/Api';

const FormaPagamento = () => {
  const [formasPagamento, setFormasPagamento] = useState([]);
  const [contas, setContas] = useState([]);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [selectedFormaPagamento, setSelectedFormaPagamento] = useState(null);
  const [formaPagamentoToDelete, setFormaPagamentoToDelete] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const total = filteredData.length;
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, total);

  useEffect(() => {
    document.title = 'Formas de Pagamento';
    fetchFormasPagamento();
    fetchContas();
  }, []);

  const sms_error = (msg) => toast.error(msg);
  const sms_success = (msg) => toast.success(msg);

  const fetchFormasPagamento = async () => {
    setLoading(true);
    try {
      const response = await Api.ListFormaPagamento();
      const dados = Array.isArray(response) ? response : [];

      const formatado = dados.map(item => ({
        key: item.id,
        id: item.id,
        descricao: item.descricao,
        matunencao: item.matunencao,
        id_conta: item.id_conta,
        banco_descricao: item.banco_descricao,
      }));

      setFormasPagamento(formatado);
      setFilteredData(formatado);
    } catch (error) {
      console.error('Erro ao buscar formas de pagamento:', error);
      sms_error('Erro ao buscar formas de pagamento: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setLoading(false);
    }
  };

  const fetchContas = async () => {
    try {
      const response = await Api.ListConta(); // Método específico para listar contas usadas em formas de pagamento
      setContas(response || []);
    } catch (error) {
      console.error('Erro ao buscar contas:', error);
      sms_error('Erro ao buscar contas: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1);

    const search = (value || '').toString().toLowerCase();
    const filtered = formasPagamento.filter(item =>
      item?.descricao?.toLowerCase().includes(search) ||
      item?.banco_descricao?.toLowerCase().includes(search)
    );

    setFilteredData(filtered);
  };

  const handleAdd = async (values) => {
    setAddLoading(true);
    try {
      const result = await Api.AddFormaPagamento({ 
        descricao: values.descricao,
        matunencao: values.matunencao,
        id_conta: values.id_conta,
      });
      
      if (result.success) {
        sms_success('Forma de pagamento adicionada com sucesso!');
        setIsAddModalVisible(false);
        form.resetFields();
        await fetchFormasPagamento();
      } else {
        sms_error(result.message || 'Erro ao adicionar forma de pagamento');
      }
    } catch (error) {
      sms_error('Erro ao adicionar forma de pagamento: ' + error.message);
    } finally {
      setAddLoading(false);
    }
  };

  const handleEdit = async (values) => {
    setEditLoading(true);
    try {
      const result = await Api.AlterFormaPagamento({
        id: selectedFormaPagamento.id,
        descricao: values.descricao,
        matunencao: values.matunencao,
        id_conta: values.id_conta,
      });
      
      if (result.success) {
        sms_success('Forma de pagamento editada com sucesso!');
        setIsEditModalVisible(false);
        editForm.resetFields();
        await fetchFormasPagamento();
      } else {
        sms_error(result.message || 'Erro ao editar forma de pagamento');
      }
    } catch (error) {
      sms_error('Erro ao editar forma de pagamento: ' + error.message);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      const result = await Api.DeleteFormaPagamento({ id: formaPagamentoToDelete });
      if (result.success) {
        sms_success('Forma de pagamento excluída com sucesso!');
        await fetchFormasPagamento();
      } else {
        sms_error(result.message || 'Erro ao excluir forma de pagamento');
      }
    } catch (error) {
      sms_error('Erro ao excluir forma de pagamento: ' + error.message);
    } finally {
      setDeleteLoading(false);
      setIsDeleteModalVisible(false);
    }
  };

  const columns = [
    {
      title: 'Descrição',
      dataIndex: 'descricao',
      key: 'descricao',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Pesquisar descrição"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              confirm();
              handleSearch(selectedKeys[0]);
            }}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button type="primary" onClick={() => {
            confirm();
            handleSearch(selectedKeys[0]);
          }} size="small" style={{ width: 90, marginRight: 8 }}>
            Filtrar
          </Button>
          <Button onClick={() => {
            clearFilters();
            handleSearch('');
          }} size="small" style={{ width: 90 }}>
            Limpar
          </Button>
        </div>
      ),
      onFilter: (value, record) =>
        record.descricao.toLowerCase().includes(value.toLowerCase())
    },
 {
      title: 'Despesa de Manutenção',
      dataIndex: 'matunencao',
      key: 'matunencao',
      render: (valor) => `${valor}`, // Mostra o valor com o símbolo de euro
      sorter: (a, b) => a.matunencao - b.matunencao,
    },
    {
      title: 'Banco',
      dataIndex: 'banco_descricao',
      key: 'banco_descricao',
      render: (text) => text || '-'
    },
    {
      title: 'Ações',
      key: 'acoes',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 10 }}>
          <FaEdit
            style={{ cursor: 'pointer', color: '#1890ff' }}
            onClick={() => {
              setSelectedFormaPagamento(record);
              editForm.setFieldsValue({
                descricao: record.descricao,
                matunencao: record.matunencao,
                id_conta: record.id_conta
              });
              setIsEditModalVisible(true);
            }}
          />
          <FaRegTrashAlt
            style={{ cursor: 'pointer', color: '#ff4d4f' }}
            onClick={() => {
              setFormaPagamentoToDelete(record.id);
              setIsDeleteModalVisible(true);
            }}
          />
        </div>
      )
    }
  ];

  return (
    <div>
      <ToastContainer />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={() => setIsAddModalVisible(true)}
        >
          Adicionar
        </Button>
      </div>

      <Spin spinning={loading} tip="Carregando...">
        <Table
          columns={columns}
          dataSource={filteredData}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: filteredData.length,
            onChange: (page, pageSize) => {
              setCurrentPage(page);
              setPageSize(pageSize);
            },
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
          }}
          scroll={{ x: 'max-content' }}
          style={{ minWidth: '100%' }}
        />
      </Spin>

      <div style={{ marginBottom: 10 }}>
        <strong>
          {total > 0
            ? `Mostrando ${startIndex}–${endIndex} de ${total} formas de pagamento`
            : 'Nenhuma forma de pagamento encontrada'}
        </strong>
      </div>

      {/* Modal Adicionar */}
      <Modal
        title="Adicionar Forma de Pagamento"
        visible={isAddModalVisible}
        onCancel={() => {
          form.resetFields();
          setIsAddModalVisible(false);
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleAdd}>
          <Form.Item
            name="descricao"
            label="Descrição"
            rules={[{ required: true, message: 'Descrição é obrigatória' }]}
          >
            <Input maxLength={50} />
          </Form.Item>
          
                 <Form.Item
            name="matunencao"
            label="Despesa de Manutenção"
          initialValue={0}
            rules={[{ required: true, message: 'Valor é obrigatório' }]}
          >
            <InputNumber
              min={0}
               precision={0}
              step={1}
               
              style={{ width: '100%' }}
              formatter={value => `${value}`}
            
              parser={value => value.replace('', '')}
            />
          </Form.Item>
          
          <Form.Item
            name="id_conta"
            label="Conta Bancária"
            rules={[{ required: true, message: 'Conta bancária é obrigatória' }]}
          >
            <Select
              showSearch
              placeholder="Selecione uma conta bancária"
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {contas.map(conta => (
                <Select.Option key={conta.id} value={conta.id}>
                  {conta.banco_descricao} - {conta.nconta}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                style={{ marginRight: 8 }}
                onClick={() => {
                  form.resetFields();
                  setIsAddModalVisible(false);
                }}
                disabled={addLoading}
              >
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit" loading={addLoading}>
                {addLoading ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Editar */}
      <Modal
        title="Editar Forma de Pagamento"
        visible={isEditModalVisible}
        onCancel={() => {
          editForm.resetFields();
          setIsEditModalVisible(false);
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          <Form.Item
            name="descricao"
            label="Descrição"
            rules={[{ required: true, message: 'Descrição é obrigatória' }]}
          >
            <Input maxLength={50} />
          </Form.Item>
          
           <Form.Item
            name="matunencao"
            label="Despesa de Manutenção"
            rules={[{ required: true, message: 'Valor é obrigatório' }]}
          >
            <InputNumber
              min={0}
              step={1}
              style={{ width: '100%' }}
                precision={0}
              formatter={value => `${value}`}
              parser={value => value.replace('', '')}
            />
          </Form.Item>
          
          <Form.Item
            name="id_conta"
            label="Conta Bancária"
            rules={[{ required: true, message: 'Conta bancária é obrigatória' }]}
          >
            <Select
              showSearch
              placeholder="Selecione uma conta bancária"
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {contas.map(conta => (
                <Select.Option key={conta.id} value={conta.id}>
                  {conta.banco_descricao} - {conta.nconta}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                style={{ marginRight: 8 }}
                onClick={() => {
                  editForm.resetFields();
                  setIsEditModalVisible(false);
                }}
                disabled={editLoading}
              >
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit" loading={editLoading}>
                {editLoading ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Exclusão */}
      <Modal
        title="Confirmar Exclusão"
        visible={isDeleteModalVisible}
        onOk={handleDelete}
        onCancel={() => setIsDeleteModalVisible(false)}
        okText="Sim"
        cancelText="Não"
        confirmLoading={deleteLoading}
      >
        <p>Tem certeza que deseja excluir esta forma de pagamento?</p>
      </Modal>
    </div>
  );
};

export default FormaPagamento;